import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/**
 * Controlled date picker.
 * Props:
 *  - value: "YYYY-MM-DD" string (ISO) or empty
 *  - onChange: (isoString) => void
 *  - placeholder
 *  - testId
 *  - disabled
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  testId,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parseISO(value) : undefined;
  const selected = parsed && isValid(parsed) ? parsed : undefined;
  const display = selected ? format(selected, "dd-MM-yyyy") : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full h-10 mt-1 flex items-center justify-between",
            "rounded-sm border border-input bg-background px-3 py-2 text-sm",
            "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "hover:border-[color:var(--aisensy-dark)]/40 transition-colors"
          )}
        >
          <span className={cn("tabular", !display && "text-muted-foreground font-normal")}>
            {display || placeholder}
          </span>
          <CalendarIcon className="w-4 h-4 text-ink-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-sm" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            } else {
              onChange("");
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
